import { useState, useEffect, useCallback } from 'react';
import {
  Save, Lock, Unlock, CalendarDays, CheckCircle2, Plane, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { C, fontSerif, fontMono, todayStr, addDays, fmtDateShort } from './utils';
import { incentivesAPI } from '../../lib/api';

export const AdminDailyUpdate = ({ canClose, onAfterClose }) => {
  const [date, setDate] = useState(todayStr());
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({}); // employee_id -> { field: value }
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await incentivesAPI.dailyGrid(d);
      setGrid(res.data);
      const init = {};
      (res.data.rows || []).forEach((r) => { init[r.employee_id] = { ...(r.inputs || {}) }; });
      setValues(init);
    } catch (e) {
      toast.error('Could not load the day');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const setVal = (empId, key, v) => {
    setValues((prev) => ({ ...prev, [empId]: { ...prev[empId], [key]: v } }));
  };

  const saveAll = async () => {
    if (!grid) return;
    setSaving(true);
    try {
      const editable = grid.rows.filter((r) => !r.on_leave && r.status !== 'CLOSED');
      await Promise.all(editable.map((r) =>
        incentivesAPI.saveDaily({ employee_id: r.employee_id, date, inputs: values[r.employee_id] || {} })
      ));
      toast.success('Saved');
      await load(date);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const closeDay = async () => {
    setClosing(true);
    try {
      await saveAllQuiet();
      await incentivesAPI.closeDay(date);
      toast.success(`${fmtDateShort(date)} closed & scored`);
      await load(date);
      onAfterClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not close the day');
    } finally {
      setClosing(false);
    }
  };

  const saveAllQuiet = async () => {
    if (!grid) return;
    const editable = grid.rows.filter((r) => !r.on_leave && r.status !== 'CLOSED');
    await Promise.all(editable.map((r) =>
      incentivesAPI.saveDaily({ employee_id: r.employee_id, date, inputs: values[r.employee_id] || {} })
    ));
  };

  const reopenDay = async () => {
    if (!reopenReason.trim()) { toast.error('A reason is required'); return; }
    try {
      await incentivesAPI.reopenDay(date, reopenReason.trim());
      toast.success('Day reopened');
      setReopenOpen(false);
      setReopenReason('');
      await load(date);
      onAfterClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reopen');
    }
  };

  const isClosed = grid?.closed;
  const isWorking = grid?.is_working_day;

  return (
    <div className="space-y-5" data-testid="admin-daily-update">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-xl font-semibold" style={{ fontFamily: fontSerif, color: C.text }}>Daily Update</h3>
          <p className="text-sm text-slate-500">Enter completed work, then close the day to lock &amp; score it.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setDate(addDays(date, -1))} data-testid="daily-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="relative">
            <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 pl-9" data-testid="daily-date-input" />
          </div>
          <Button variant="outline" size="icon" onClick={() => setDate(addDays(date, 1))} data-testid="daily-next-day"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* status banner */}
      <div className="flex flex-wrap items-center gap-3">
        {!isWorking && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">Not a working day</span>
        )}
        {isClosed ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-200" data-testid="day-closed-badge">
            <Lock className="h-3.5 w-3.5" /> Day closed & scored
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 border border-amber-200">
            <Unlock className="h-3.5 w-3.5" /> Open for editing
          </span>
        )}
      </div>

      {loading ? (
        <div className="rounded-2xl border bg-white p-10 text-center text-sm text-slate-400" style={{ borderColor: C.border }}>Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border bg-white" style={{ borderColor: C.border }} data-testid="admin-daily-update-grid">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: C.border }}>
                <th className="px-4 py-3 font-semibold text-slate-500">Teammate</th>
                <th className="px-4 py-3 font-semibold text-slate-500">Target</th>
                <th className="px-4 py-3 font-semibold text-slate-500">Inputs</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {(grid?.rows || []).map((r) => {
                const rowClosed = r.status === 'CLOSED' || isClosed;
                return (
                  <tr key={r.employee_id} className="border-b last:border-0" style={{ borderColor: C.border }} data-testid={`daily-row-${r.employee_id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold" style={{ color: C.text }}>{r.name}</div>
                      <div className="text-xs text-slate-400">{r.role_title}</div>
                      {r.on_leave && (
                        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                          <Plane className="h-3 w-3" /> On leave
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className="text-lg font-bold tabular-nums" style={{ fontFamily: fontMono, color: C.burgundy }}>{r.daily_target}</span>
                      <div className="text-xs text-slate-400">{r.output_unit_label}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {r.on_leave ? (
                        <span className="text-xs text-slate-400">No entry needed (approved leave)</span>
                      ) : (
                        <div className="flex flex-wrap gap-3">
                          {r.fields.map((f) => (
                            <label key={f.key} className="flex flex-col">
                              <span className="mb-0.5 text-[11px] text-slate-500">{f.label}</span>
                              <Input
                                type="number"
                                min="0"
                                inputMode="numeric"
                                disabled={rowClosed}
                                value={values[r.employee_id]?.[f.key] ?? ''}
                                onChange={(e) => setVal(r.employee_id, f.key, e.target.value)}
                                className="h-9 w-24 tabular-nums"
                                style={{ fontFamily: fontMono }}
                                data-testid={`daily-input-${r.employee_id}-${f.key}`}
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      {rowClosed && !r.on_leave && <CheckCircle2 className="ml-auto h-5 w-5 text-emerald-500" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* actions */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        {isClosed ? (
          canClose && (
            <Button variant="outline" onClick={() => setReopenOpen(true)} data-testid="reopen-day-button">
              <Unlock className="mr-1.5 h-4 w-4" /> Reopen day
            </Button>
          )
        ) : (
          <>
            <Button onClick={saveAll} disabled={saving || !isWorking} variant="outline" data-testid="bulk-save-button">
              <Save className="mr-1.5 h-4 w-4" /> {saving ? 'Saving…' : 'Save all'}
            </Button>
            {canClose && (
              <Button onClick={closeDay} disabled={closing || !isWorking} className="text-white" style={{ background: C.burgundy }} data-testid="close-day-button">
                <Lock className="mr-1.5 h-4 w-4" /> {closing ? 'Closing…' : 'Close day'}
              </Button>
            )}
          </>
        )}
      </div>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="reopen-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: fontSerif }}>Reopen {fmtDateShort(date)}?</DialogTitle>
            <DialogDescription>This clears the day&apos;s scores until you close it again. A reason is logged.</DialogDescription>
          </DialogHeader>
          <Input value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="Reason (required)" data-testid="reopen-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)}>Cancel</Button>
            <Button onClick={reopenDay} className="text-white" style={{ background: C.burgundy }} data-testid="reopen-confirm-btn">Reopen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
